import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@ipc/ipc-components';
import { Redirect, Link } from '@ipc/ipc-core';

import { Loading } from '../../elements';
import {
  AppContentInner, AppContentTitle, TopButtonsWrapper,
} from '../../styles';
import { START_DATE } from './constants';
import { createAlaAgreement, getAlaAgreement, updateAlaAgreement } from '../../api';
import { dateToString, formatDate } from '../../utils/date';
import { useFormOptions } from '../../hooks';

import AlaLevelsSection from './sections/AlaLevels';
import RouteSection from './sections/Route';
import ValidityPeriodSection from './sections/ValidityPeriod';
import AutomaticAlaApprovalSection from './sections/AutomaticAlaApproval';
import { getValues, getPayload, validateDates } from './helpers';
import { useValidations } from './hooks';
import AlaTableView from '../../components/custom/AlaTableView';
import { MarginTopWrapper } from './styles';
import {
  ALA_LEVELS_OPTIONS_WITHOUT_ALL, DISAPPROVED, MAX_ALA_LEVEL, PENDING,
} from '../../utils/constants';
import { cartesianProduct } from '../../utils/arrayUtils';

const CreateAlaLevel = ({
  match: { params: { id } }, location, newError, newWarning,
}) => {
  const [options, loaded] = useFormOptions(['iata', 'dropdown_carrier', 'postal_operator']);
  const [saved, updateSaved] = useState(false);
  const [submitted, updateSubmitted] = useState(false);
  const [saving, updateSaving] = useState(false);
  const [values, updateValues] = useState({
    alaLevels: ALA_LEVELS_OPTIONS_WITHOUT_ALL,
    startDate: formatDate(START_DATE),
  });
  const [isAllAlaLevelsChecked, setAllAlaLevelsChecked] = useState(true);
  const [validations] = useValidations(values, submitted);
  const [splitSelectDisabled, updateSplitSelectDisabled] = useState(false);
  const [isEdit] = useState(location.pathname.includes('edit'));
  const [isCopy] = useState(location.pathname.includes('copy'));

  const isStatusPendingOrDisapproved = (response) => {
    if (response && response.status) {
      return [PENDING, DISAPPROVED].some(str => response.status.includes(str));
    }
    return false;
  };

  useEffect(() => {
    (async () => {
      if (id) {
        const response = await getAlaAgreement(id);

        if (!response) {
          newError('ALA Level Agreement was not found!', 5);
          updateSaved(true);
          return;
        }
        if (isEdit && !isStatusPendingOrDisapproved(response)) {
          updateSplitSelectDisabled(true);
        }

        setAllAlaLevelsChecked(false);
        const mappedValues = getValues(response);
        if (isCopy) {
          mappedValues.startDate = formatDate(START_DATE);
          mappedValues.stopDate = null;
        }
        if (mappedValues.alaLevels && mappedValues.alaLevels.length === MAX_ALA_LEVEL) {
          setAllAlaLevelsChecked(true);
        }
        updateValues(mappedValues);
      }
    })();
  }, [id]);

  const selectChangeHandler = (selected, { name }) => {
    updateValues({
      ...values,
      [name]: selected || [],
    });
  };

  const alaLevelsChangeHandler = (selected = [], { name }) => {
    const { autoApprovalAlaLevel } = values;

    if (!selected
      || (autoApprovalAlaLevel && !selected.some(({ value }) => value === autoApprovalAlaLevel.value))) {
      values.autoApprovalAlaLevel = null;
    }

    selectChangeHandler(selected, { name });
  };

  const dateChangeHandler = name => (selectedDay, modifiers, dayPickerInput) => {
    const input = dayPickerInput.getInput().value.trim();
    const isDisabled = modifiers.disabled === true;
    if (selectedDay && (selectedDay.getFullYear() <= 9999) && input && input !== '' && !isDisabled) {
      updateValues({ ...values, [name]: dateToString(selectedDay) });
    } else {
      updateValues({ ...values, [name]: null });
    }
  };

  const checkboxChangeHandler = ({ target: { name } }, value) => {
    updateValues({
      ...values,
      [name]: value,
    });
  };

  const allAlaCheckboxHandler = (event, value) => {
    setAllAlaLevelsChecked(value);
    if (value === true) {
      updateValues({
        ...values,
        alaLevels: ALA_LEVELS_OPTIONS_WITHOUT_ALL,
      });
    } else {
      values.autoApprovalAlaLevel = null;
      updateValues({
        ...values,
        alaLevels: [],
      });
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    updateSubmitted(true);

    if (!validations.all) {
      newWarning('Please fill all required fields and try again!', 5);
      return;
    }

    const isOriginDifferentThanAll = () => values.origin && values.origin.length && values.origin[0].value !== 'All';
    const isOriginAndDestinationEqual = () => JSON.stringify(values.origin) === JSON.stringify(values.destination);

    if (isOriginDifferentThanAll() && isOriginAndDestinationEqual()) {
      newWarning('Origin and Destination locations can not be the same');
      return;
    }

    if (!validateDates(values, newWarning)) {
      return;
    }

    const payload = getPayload(values);
    const operators = Array.isArray(payload.postalOperator) ? payload.postalOperator : [payload.postalOperator];
    const carriers = Array.isArray(payload.carrier) ? payload.carrier : [payload.carrier];
    const cartesianArray = cartesianProduct(operators, carriers);
    const resultList = cartesianArray.map(([postalOperator, carrier]) => ({ ...payload, postalOperator, carrier }));
    updateSaving(true);
    let response;
    if (id && isEdit) {
      response = await updateAlaAgreement(resultList[0]);
    } else {
      response = await createAlaAgreement(resultList.map(item => ({ ...item, id: undefined })));
    }
    updateSaving(false);

    if (response) {
      updateSaved(true);
    }
  };

  return saved ? (
    <Redirect to="/configuration/agreements" />
  ) : (
    <AppContentInner>
      <AppContentTitle>
        {id ? 'Edit ' : 'Create '}
        ALA Level Agreement
      </AppContentTitle>
      <MarginTopWrapper>
        <TopButtonsWrapper>
          <AlaTableView />
        </TopButtonsWrapper>
      </MarginTopWrapper>
      <form className="Form" onSubmit={submitHandler}>
        <Loading show={!loaded || saving} />
        <AlaLevelsSection
          values={values}
          selectChangeHandler={alaLevelsChangeHandler}
          validations={validations}
          allAlaLevelsChecked={isAllAlaLevelsChecked}
          allAlaLevelsCheckboxHandler={allAlaCheckboxHandler}
        />
        <br />
        <AutomaticAlaApprovalSection values={values} selectChangeHandler={selectChangeHandler} checkboxChangeHandler={checkboxChangeHandler} validations={validations} />
        <br />
        <RouteSection disabled={splitSelectDisabled} options={options} values={values} selectChangeHandler={selectChangeHandler} validations={validations} />
        <br />
        <ValidityPeriodSection values={values} dateChangeHandler={dateChangeHandler} checkboxChangeHandler={checkboxChangeHandler} validations={validations} isCopy={isCopy} />

        <div className="ButtonWrapper">
          <Link to="/configuration/agreements">
            <Button className="CancelButton">Cancel</Button>
          </Link>
          <Button className="SaveButton" disabled={!loaded} data-testid="submit-action">Submit</Button>
        </div>
      </form>
    </AppContentInner>
  );
};

CreateAlaLevel.propTypes = {
  match: PropTypes.shape(),
  newError: PropTypes.func,
  newWarning: PropTypes.func,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
};

export default CreateAlaLevel;
